import apiClient from './client';
import type { MatchResult, TenderMatchSummary, PaginatedResponse } from '../types';

// -------------------------------------------------------------------------
// Query params
// -------------------------------------------------------------------------

export interface MatchListParams {
  page?: number;
  page_size?: number;
  min_score?: number;
  tier?: MatchResult['tier'];
  sort_by?: 'score' | 'created_at' | 'employee_name';
  order?: 'asc' | 'desc';
}

// -------------------------------------------------------------------------
// Backend → frontend mapping
// -------------------------------------------------------------------------

function parseSkills(value: unknown): string[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.startsWith('[')) {
    try { return JSON.parse(value); } catch { /* fall through */ }
  }
  return [];
}

function scoreTier(score: number): MatchResult['tier'] {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'partial';
  return 'poor';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapMatchResult(raw: any): MatchResult {
  // Backend returns final_score as 0-1, frontend expects score as 0-100
  const finalScore = raw.final_score ?? raw.score ?? 0;
  const score = finalScore > 1 ? finalScore : Math.round(finalScore * 100);
  const matchedSkills = parseSkills(raw.matched_skills);

  return {
    id: raw.id,
    tender_id: raw.tender_id ?? raw.tender ?? '',
    employee_id: raw.employee_id ?? raw.employee ?? '',
    employee_name: raw.employee_name ?? '',
    employee_role: '',
    score,
    tier: scoreTier(score),
    matched_skills: matchedSkills,
    missing_skills: parseSkills(raw.missing_skills),
    nice_to_have_matched: [],
    skill_breakdown: matchedSkills.map((s: string) => ({
      skill: s, matched: true, is_required: true,
    })),
    explanation: raw.llm_explanation ?? raw.explanation ?? '',
    strengths: raw.strengths ?? '',
    role: raw.role ?? '',
    created_at: raw.created_at ?? '',
  };
}

// -------------------------------------------------------------------------
// API functions
// -------------------------------------------------------------------------

/**
 * Fetch the full match summary (all results) for a given tender.
 */
export async function fetchMatchSummary(tenderId: string): Promise<TenderMatchSummary> {
  const { data } = await apiClient.get<TenderMatchSummary>(
    `/matches/tenders/${tenderId}/summary`
  );
  return data;
}

/**
 * Fetch paginated match results for a given tender.
 * Maps Spring Page response to frontend PaginatedResponse.
 */
export async function fetchMatchResults(
  tenderId: string,
  params: MatchListParams = {}
): Promise<PaginatedResponse<MatchResult>> {
  const sortMap: Record<string, string> = { score: 'finalScore', created_at: 'createdAt', employee_name: 'employee.name' };
  const sortField = sortMap[params.sort_by ?? 'score'] ?? 'finalScore';
  const springParams: Record<string, unknown> = {
    page: (params.page ?? 1) - 1,
    size: params.page_size ?? 20,
    ...(params.min_score !== undefined ? { minScore: params.min_score } : {}),
    ...(params.tier ? { tier: params.tier } : {}),
    sort: `${sortField},${params.order ?? 'desc'}`,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await apiClient.get<any>(
    `/matches/tenders/${tenderId}`,
    { params: springParams }
  );

  // Handle both Spring Page and direct array responses
  if (Array.isArray(data)) {
    return { items: data.map(mapMatchResult), total: data.length, page: 1, page_size: data.length, total_pages: 1 };
  }
  return {
    items: (data.content ?? []).map(mapMatchResult),
    total: data.total_elements ?? 0,
    page: (data.number ?? 0) + 1,
    page_size: data.size ?? 20,
    total_pages: data.total_pages ?? 0,
  };
}

/**
 * Fetch a single match result by its ID.
 */
export async function fetchMatchResult(matchId: string): Promise<MatchResult> {
  const { data } = await apiClient.get(`/matches/${matchId}`);
  return mapMatchResult(data);
}

/**
 * Export match results for a tender as CSV (returns a Blob).
 */
export async function exportMatchResultsCsv(tenderId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/matches/tenders/${tenderId}/export`,
    { responseType: 'blob' }
  );
  return data;
}

/**
 * Fetch all match results across all tenders for a specific employee.
 */
export async function fetchEmployeeMatches(
  employeeId: string,
  params: MatchListParams = {}
): Promise<PaginatedResponse<MatchResult>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await apiClient.get<any>(
    `/matches/employees/${employeeId}`,
    { params }
  );

  if (Array.isArray(data)) {
    return { items: data.map(mapMatchResult), total: data.length, page: 1, page_size: data.length, total_pages: 1 };
  }
  return {
    items: (data.content ?? []).map(mapMatchResult),
    total: data.total_elements ?? 0,
    page: (data.number ?? 0) + 1,
    page_size: data.size ?? 20,
    total_pages: data.total_pages ?? 0,
  };
}
